import { useLanguage } from '@/contexts/language-context';
import { formatMoney } from '@/lib/utils';

import {
  formatDateTime,
  lineItemsTotal,
  quotePhase,
  quotePhaseColorClass,
  quotePhaseLabel,
} from './helpers';
import type { Quote } from './types';

interface Props {
  quote: Quote;
}

// Read-only, invoice-style rendering of a quote — the "actual quote" visual,
// so it can be reviewed without a stored PDF.
export function QuoteDocument({ quote }: Props) {
  const { t } = useLanguage();
  const phase = quotePhase(quote);
  const itemsTotal = lineItemsTotal(quote.line_items);
  const overridden = quote.line_items.length > 0 && itemsTotal !== quote.total_amount;

  const meta: string[] = [`${t.quoteCreatedAt} ${formatDateTime(quote.created_at)}`];
  if (quote.sent_at) meta.push(`${t.quoteSentAt} ${formatDateTime(quote.sent_at)}`);
  if (quote.approved_at) meta.push(`${t.quoteApprovedAt} ${formatDateTime(quote.approved_at)}`);
  if (quote.responded_at)
    meta.push(`${t.quoteRespondedAt} ${formatDateTime(quote.responded_at)}`);

  return (
    <div
      data-testid="quote-document"
      className="rounded-lg border bg-background p-5 flex flex-col gap-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold leading-tight">{t.quotesTitle}</h3>
          {quote.job_title && (
            <p className="text-sm text-muted-foreground truncate">{quote.job_title}</p>
          )}
        </div>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${quotePhaseColorClass(phase)}`}
          data-testid="quote-phase"
        >
          {quotePhaseLabel(phase, t)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground -mt-3">{meta.join(' · ')}</p>

      {/* Line items */}
      <div>
        <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-muted-foreground border-b pb-1.5">
          <span>{t.quoteLineItemDescription}</span>
          <span>{t.quoteLineItemAmount}</span>
        </div>
        {quote.line_items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3">{t.quoteNoLineItems}</p>
        ) : (
          <ul className="divide-y">
            {quote.line_items.map((item, i) => (
              <li
                // Line items have no server id and this list is static/read-only.
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${item.description}-${item.amount}-${i}`}
                className="flex items-start justify-between gap-4 py-2 text-sm"
              >
                <span className="min-w-0">{item.description}</span>
                <span className="shrink-0 tabular-nums">{formatMoney(item.amount)}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-2 flex flex-col gap-1 border-t pt-2 text-sm">
          {overridden && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span>{t.quoteLineItemsReconcile}</span>
              <span className="tabular-nums">{formatMoney(itemsTotal)}</span>
            </div>
          )}
          <div className="flex items-center justify-between font-semibold">
            <span>{t.quoteTotal}</span>
            <span className="tabular-nums" data-testid="quote-doc-total">
              {formatMoney(quote.total_amount)}
            </span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {quote.notes && (
        <div className="flex flex-col gap-1">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t.quoteNotes}
          </p>
          <p className="text-sm whitespace-pre-wrap" data-testid="quote-doc-notes">
            {quote.notes}
          </p>
        </div>
      )}
    </div>
  );
}
